import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { setUnauthorizedHandler } from '../apiClient.js'
import SessionExpiredModal from '../components/SessionExpiredModal'

const AuthContext = createContext(null)

// The server is the single source of truth for session lifetime (see
// functions/api/_middleware.js — sessions now slide their own expiry on use,
// capped at 7 days from creation). The client used to also track its own
// expiry guess in localStorage and auto-logout on a timer; that guess drifted
// from the server's real, sliding expiry and caused a real incident (the
// browser believed it was still signed in for hours after the server had
// started 401ing it, and the failure surfaced as a generic data-loading
// error instead of a re-auth prompt). So the client no longer predicts
// expiry at all — it just reacts to an authoritative 401 from the server,
// via setUnauthorizedHandler below.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false)
  // Bumped on every successful re-auth (both the ordinary login() flow and
  // the session-expired modal's reauthenticate()) so components that fetch
  // their own data can add it as a useEffect dependency and refetch. This is
  // the other half of the 401 fix: raising the modal alone re-establishes a
  // valid token, but any component that already ran its fetch-on-mount
  // effect and landed in an error/empty state before the 401 was noticed
  // would otherwise sit there forever — a manual refresh was the only way
  // out, which is the exact symptom this whole change exists to fix.
  const [authEpoch, setAuthEpoch] = useState(0)
  // Admin-only "view as player" mode: lets an admin preview the site exactly as a
  // player would see it (no admin controls, hidden content filtered out) without
  // logging out. Persisted so it survives a refresh.
  const [viewAsPlayer, setViewAsPlayer] = useState(() => localStorage.getItem('viewAsPlayer') === '1')

  const toggleViewAsPlayer = useCallback(() => {
    setViewAsPlayer(prev => {
      const next = !prev
      localStorage.setItem('viewAsPlayer', next ? '1' : '0')
      return next
    })
  }, [])

  // Logout function
  const logout = useCallback(() => {
    // Fire the logout request first so the interceptor still has the token in
    // localStorage to attach (lets the server revoke the session).
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {})
    setUser(null)
    setViewAsPlayer(false)
    setSessionExpiredModalOpen(false)
    localStorage.removeItem('user')
    localStorage.removeItem('sessionExpiry')
    localStorage.removeItem('sessionToken')
    localStorage.removeItem('viewAsPlayer')
  }, [])

  // Load user from localStorage on mount. The stored token's actual validity
  // is the server's call, not something the client pre-checks — the first
  // API request will either succeed or come back 401 (raising the
  // session-expired modal via the handler registered below).
  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    const storedToken = localStorage.getItem('sessionToken')

    // Require a session token. Sessions stored before server-side auth existed
    // won't have one — treat them as stale and force a fresh login.
    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser))
      } catch (error) {
        console.error('Failed to parse stored user:', error)
        localStorage.removeItem('user')
        localStorage.removeItem('sessionToken')
      }
    } else if (storedUser || storedToken) {
      // Partial/stale session — clear it.
      localStorage.removeItem('user')
      localStorage.removeItem('sessionToken')
    }
    // Legacy key from the old client-side expiry guess — no longer read, but
    // clear it so it doesn't linger forever in localStorage.
    localStorage.removeItem('sessionExpiry')
    setLoading(false)
  }, [])

  // Kept in a ref (rather than a dependency of the handler-registration
  // effect below) so the 401 handler always sees the latest `user` without
  // needing to re-register itself — and so a 401 that arrives while there is
  // no logged-in user (e.g. a stray request from an already-logged-out tab)
  // doesn't pop the modal over nothing.
  const userRef = useRef(null)
  useEffect(() => {
    userRef.current = user
  }, [user])

  // Register with the fetch interceptor (src/apiClient.js) so a 401 from any
  // /api/* request raises the re-auth modal instead of the page silently
  // failing. apiClient.js does not import this file — only this file imports
  // apiClient.js — so there's no circular import; apiClient just holds a
  // callback slot that whoever cares (this context) fills in.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (userRef.current) setSessionExpiredModalOpen(true)
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  const login = async (username, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setUser(data.user)
        localStorage.setItem('user', JSON.stringify(data.user))
        if (data.token) localStorage.setItem('sessionToken', data.token)
        setAuthEpoch((prev) => prev + 1)
        return { success: true }
      } else {
        return {
          success: false,
          error: data.error || 'Login failed',
          locked: data.locked,
          attemptsRemaining: data.attemptsRemaining,
          minutesRemaining: data.minutesRemaining
        }
      }
    } catch (error) {
      return { success: false, error: 'Network error' }
    }
  }

  // Used by the session-expired modal to sign back in over top of whatever
  // page the user was on, without unmounting anything or losing route/form
  // state — just swaps the token and closes the modal.
  const reauthenticate = async (username, password) => {
    const result = await login(username, password)
    if (result.success) {
      setSessionExpiredModalOpen(false)
    }
    return result
  }

  // realIsAdmin reflects the account's true role; isAdmin is the *effective* value
  // used throughout the UI to gate admin controls and reveal hidden content. When
  // an admin enables "view as player", isAdmin drops to false so every consumer
  // behaves as it would for a real player — no other code needs to know about the toggle.
  const realIsAdmin = user?.role === 'admin'
  const isAdmin = realIsAdmin && !viewAsPlayer
  const isPlayer = user?.role === 'player'

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      isAdmin,
      realIsAdmin,
      isPlayer,
      viewAsPlayer,
      toggleViewAsPlayer,
      authEpoch,
    }}>
      {children}
      <SessionExpiredModal
        isOpen={sessionExpiredModalOpen}
        onClose={() => setSessionExpiredModalOpen(false)}
        onLogin={reauthenticate}
        onSignOut={logout}
        username={user?.username}
      />
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
